import { z } from 'zod';
/**
 * Hotfix 34 — "Need immediate support now between 10–20 min" on the standard
 * IT ticket form. A request for a prompt call-back / contact inside the
 * working-hours window; it does NOT change SLA targets, routing approval or
 * resolution guarantees. Availability is decided server-side from the same
 * business calendar the IT-ticket SLA uses (Mon–Fri 10:00–18:00 in the
 * calendar's timezone, default Asia/Kolkata).
 */
export const IMMEDIATE_SUPPORT_HEADING = 'Need immediate support now between 10–20 min';
export const IMMEDIATE_SUPPORT_AVAILABILITY_TEXT = 'This facility is available between 10am–6pm, Monday to Friday.';
export const IMMEDIATE_SUPPORT_CHECKBOX_LABEL = 'Yes, I need immediate support';
export const IMMEDIATE_SUPPORT_UNAVAILABLE_MESSAGE = 'Immediate support is only available 10am–6pm, Monday to Friday. Untick it to submit this as an ordinary ticket — everything you entered is kept.';
/** Field-error code the API returns (field `immediateSupport`) when the window closed before submit. */
export const IMMEDIATE_SUPPORT_UNAVAILABLE_ISSUE = 'immediate_support_unavailable';
export const MOBILE_NUMBER_MAX_LENGTH = 20;
const MOBILE_INPUT_RE = /^\+?[0-9][0-9 ()-]{5,22}[0-9]$/;
/**
 * Normalises a mobile number as typed: keeps a leading "+" (country code) and
 * the digits, drops spaces / hyphens / brackets. Returns null when it is not a
 * plausible phone number (7–15 digits, E.164 length). Never assumes a country:
 * "+44 7700 900123", "98765 43210" and "+91-98765-43210" are all accepted.
 */
export function normaliseMobileNumber(raw) {
    if (raw == null)
        return null;
    const trimmed = raw.trim().replace(/\s+/g, ' ');
    if (!MOBILE_INPUT_RE.test(trimmed))
        return null;
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15)
        return null;
    return (trimmed.startsWith('+') ? '+' : '') + digits;
}
export const MOBILE_NUMBER_ERROR = 'Enter a valid mobile number, e.g. +91 98765 43210.';
/** Masks a number for diagnostics: never log the full number. "+919876543210" -> "+91******210". */
export function maskPhone(n) {
    if (!n)
        return '';
    if (n.length <= 6)
        return '***';
    return n.slice(0, 3) + '*'.repeat(Math.max(3, n.length - 6)) + n.slice(-3);
}
/** On `POST /tickets` (standard form). Absent / requested:false = an ordinary ticket. */
export const immediateSupportRequestSchema = z
    .object({
    requested: z.boolean(),
    mobileNumber: z.string().trim().max(40).nullish(),
})
    .strict()
    .superRefine((d, ctx) => {
    if (!d.requested) {
        if (d.mobileNumber && d.mobileNumber.trim() !== '') {
            // A number with immediate support off is simply ignored by the service; not an error.
        }
        return;
    }
    if (!d.mobileNumber || d.mobileNumber.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mobileNumber'], message: 'Enter a mobile number so the IT team can call you.' });
        return;
    }
    if (!normaliseMobileNumber(d.mobileNumber)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mobileNumber'], message: MOBILE_NUMBER_ERROR });
    }
});
/** `GET /tickets/immediate-support/availability` — server time, never the browser's. */
export const immediateSupportAvailabilityDtoSchema = z.object({
    available: z.boolean(),
    /** Server instant the answer was computed at. */
    checkedAt: z.date(),
    /** Next instant the window opens (null while open). */
    nextOpensAt: z.date().nullable(),
    /** Instant the current window closes (null while closed). */
    closesAt: z.date().nullable(),
    timezone: z.string(),
});
/** On ticket detail. `mobileNumber` is null for viewers who may not see it (anyone but the requester and IT staff). */
export const immediateSupportRecordSchema = z.object({
    requestedAt: z.date(),
    mobileNumber: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// "How many people are affected?" — display labels for the existing impact
// rows. Internal values (the HIGH/MEDIUM/LOW impact rows, their ids, ranks and
// the priority matrix) are unchanged; only what the form shows changes.
// "Not sure" is the empty choice (no impact) and is never mapped to Low.
// ---------------------------------------------------------------------------
export const IMPACT_NOT_SURE_LABEL = 'Not sure';
export const IMPACT_OPTION_LABELS = {
    HIGH: 'High — 4 or more people',
    MEDIUM: 'Medium — 3 people',
    LOW: 'Low — 1–2 people',
};
/** Display order on the form, after "Not sure". Codes not listed keep their admin-defined name and rank order after these. */
export const IMPACT_OPTION_ORDER = ['HIGH', 'MEDIUM', 'LOW'];
export function impactOptionLabel(code, name) {
    return (code && IMPACT_OPTION_LABELS[code]) || name;
}
/** Affected-person count -> impact code: 1–2 Low, 3 Medium, 4+ High. Null for anything that is not a positive integer. */
export function impactCodeForAffectedCount(count) {
    if (count == null || !Number.isInteger(count) || count < 1)
        return null;
    if (count <= 2)
        return 'LOW';
    if (count === 3)
        return 'MEDIUM';
    return 'HIGH';
}
// ---------------------------------------------------------------------------
// Update (hotfix 34): immediate support is offered ONLY for the Software
// category. The section is hidden for every other category (incl. CCTV and
// IP Whitelisting) and the API refuses `immediateSupport.requested` for them.
// Categories are admin-managed on the live server, so "Software" is matched
// by its code SOFTWARE or, failing that, its name "Software" (case/space-insensitive).
// ---------------------------------------------------------------------------
export const IMMEDIATE_SUPPORT_CATEGORY_CODE = 'SOFTWARE';
export const IMMEDIATE_SUPPORT_CATEGORY_NAME = 'Software';
/** Field-error issue (field `immediateSupport`) when requested for a non-Software category. */
export const IMMEDIATE_SUPPORT_SOFTWARE_ONLY_ISSUE = 'immediate_support_software_only';
export function isImmediateSupportCategory(category) {
    if (!category)
        return false;
    if ((category.code ?? '').trim().toUpperCase() === IMMEDIATE_SUPPORT_CATEGORY_CODE)
        return true;
    return (category.name ?? '').trim().replace(/\s+/g, ' ').toLowerCase() === IMMEDIATE_SUPPORT_CATEGORY_NAME.toLowerCase();
}
//# sourceMappingURL=immediate-support.js.map